/** SessionStorage 工具函数 */
import { isServer } from "solid-js/web";

/**
 * 设置 sessionStorage 项
 *
 * @param key 键名
 * @param value 值
 */
export const setSessionStorageItem = <T>(key: string, value: T): void => {
  try {
    const serializedValue =
      value instanceof Object ? JSON.stringify(value) : value;
    sessionStorage.setItem(key, serializedValue as string);
  } catch (error) {
    console.error("Error saving to sessionStorage:", error);
  }
};

/**
 * 获取 sessionStorage 项
 *
 * @param key 键名
 * @param defaultValue 默认值
 * @returns 存储的值或默认值
 */
export const getSessionStorageItem = <T>(key: string, defaultValue: T): T => {
  try {
    if (!isServer) {
      const item = sessionStorage.getItem(key);
      if (item === null) return defaultValue;

      try {
        return JSON.parse(item);
      } catch {
        // 如果不是有效的JSON，直接返回字符串值
        return item as unknown as T;
      }
    }
    return defaultValue;
  } catch (error) {
    console.error("Error reading from sessionStorage:", error);
    return defaultValue;
  }
};

/**
 * 删除 sessionStorage 项
 *
 * @param key 键名
 */
export const removeSessionStorageItem = (key: string): void => {
  try {
    if (!isServer) {
      // 添加服务器端检查
      sessionStorage.removeItem(key);
    }
  } catch (error) {
    console.error("Error removing from sessionStorage:", error);
  }
};

/** 清空所有 sessionStorage 项 */
export const clearSessionStorage = (): void => {
  try {
    if (!isServer) {
      // 添加服务器端检查
      sessionStorage.clear();
    }
  } catch (error) {
    console.error("Error clearing sessionStorage:", error);
  }
};

/**
 * 获取所有 sessionStorage 键名
 *
 * @returns 键名数组
 */
export const getAllSessionStorageKeys = (): string[] => {
  try {
    return Object.keys(sessionStorage);
  } catch (error) {
    console.error("Error getting sessionStorage keys:", error);
    return [];
  }
};

/**
 * 检查 sessionStorage 是否可用
 *
 * @returns 是否可用
 */
export const isSessionStorageAvailable = (): boolean => {
  try {
    const test = "__storage_test__";
    sessionStorage.setItem(test, test);
    sessionStorage.removeItem(test);
    return true;
  } catch (error) {
    console.error("Error checking sessionStorage availability:", error);
    return false;
  }
};
